import { useEffect, useState } from "react";
import { motion, animate, useMotionValue } from "framer-motion";
import { Download, Copy, Check, AppWindow, Hash, HardDrive, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API, formatBytes } from "@/lib/api";

export const CountUp = ({ value, testid }) => {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    const controls = animate(mv, value, { duration: 1.4, ease: [0.16, 1, 0.3, 1] });
    const unsub = mv.on("change", (v) => setDisplay(Math.round(v).toLocaleString()));
    return () => { controls.stop(); unsub(); };
  }, [value, mv]);
  return <span data-testid={testid}>{display}</span>;
};

export const DownloadSection = ({ info }) => {
  const [copied, setCopied] = useState(false);

  const copySha = async () => {
    if (!info.sha256) return;
    try {
      await navigator.clipboard.writeText(info.sha256);
      setCopied(true);
      toast.success("SHA-256 fingerprint copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <section id="download" className="relative py-24 px-4 sm:px-6 lg:px-8" data-testid="download-section">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-mint/[0.05] blur-[130px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14"
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-mint mb-4">Deploy the vault</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100" data-testid="download-heading">
            Download Cyrion for <span className="gold-text">Windows</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="glass-panel rounded-3xl p-8 sm:p-12 relative overflow-hidden grain"
          data-testid="download-card"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/[0.08] blur-[90px] pointer-events-none" />
          <div className="grid md:grid-cols-[1fr_auto] gap-10 items-center relative">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gold/10 border border-gold/25 flex items-center justify-center">
                  <AppWindow className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-100" data-testid="download-file-name">
                    {info.available ? info.filename : "Cyrion Wallet — Windows"}
                  </h3>
                  <p className="font-mono text-xs text-slate-500 mt-1" data-testid="download-meta">
                    {info.available
                      ? `v${info.version} · ${formatBytes(info.size)} · Windows 10/11 64-bit`
                      : "Windows 10/11 64-bit · build publishing shortly"}
                  </p>
                </div>
              </div>

              {info.sha256 && (
                <button
                  onClick={copySha}
                  data-testid="sha-copy-btn"
                  className="group flex items-center gap-3 max-w-full bg-ink/70 border border-white/10 hover:border-mint/40 rounded-xl px-4 py-3 transition-colors duration-300"
                >
                  <Hash className="w-4 h-4 text-mint shrink-0" />
                  <span className="font-mono text-[11px] text-slate-400 truncate" data-testid="sha-value">{info.sha256}</span>
                  {copied ? <Check className="w-4 h-4 text-mint shrink-0" /> : <Copy className="w-4 h-4 text-slate-500 group-hover:text-mint shrink-0 transition-colors" />}
                </button>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-6">
                {info.available ? (
                  <a
                    href={`${API}/download/file`}
                    data-testid="download-windows-btn"
                    className="group flex items-center gap-3 bg-gold text-ink font-display font-bold text-sm px-8 py-4 rounded-full hover:bg-gold-soft hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
                    Download Installer
                  </a>
                ) : (
                  <span
                    data-testid="download-windows-btn-disabled"
                    className="flex items-center gap-3 bg-white/5 text-slate-500 font-display font-bold text-sm px-8 py-4 rounded-full border border-white/10 cursor-not-allowed"
                  >
                    <HardDrive className="w-4 h-4" />
                    Installer arriving shortly
                  </span>
                )}
                {info.updated_at && (
                  <span className="flex items-center gap-2 font-mono text-[11px] text-slate-500" data-testid="download-updated-at">
                    <RefreshCw className="w-3 h-3" />
                    Updated {new Date(info.updated_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="text-center md:text-right md:pl-10 md:border-l border-white/[0.07]">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-2">Secured downloads</p>
              <p className="font-display text-5xl sm:text-6xl font-extrabold gold-text leading-none" data-testid="download-counter">
                <CountUp value={info.download_count || 0} testid="download-count-value" />
              </p>
              <p className="font-mono text-[11px] text-mint mt-3 flex items-center justify-center md:justify-end gap-2" data-testid="counter-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-mint animate-pulse" />
                Live counter
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
